// Reglas puras del panel «Datos legales» de Administración · Empresa
// (handoff §2, plan 2026-10-30 §2.2, T-17p9): qué cuenta como cambio y
// qué viaja en el PATCH /organizations/me. Sin React, para que la
// pantalla y sus tests hablen de un solo cálculo.
//
// El RUT se compara como texto contra lo que se mostró al abrir (la forma
// con puntos de formatRut), no contra la forma canónica guardada: así un
// RUT heredado que no cuadra en módulo 11 no marca el formulario como
// «con cambios» hasta que alguien lo toque. Al guardar se envía
// normalizado («12345678-5»); el servidor vuelve a validar y responde
// 400 RUT_INVALID si no cuadra.

import type { MyOrganizationUpdateBody } from "@bitacorapro/api-types";
import { formatRut, normalizeRut } from "@bitacorapro/shared/rut";

export type DatosLegalesDraft = { name: string; rut: string };

export const RUT_INVALID_MESSAGE = "RUT no válido";
export const NAME_REQUIRED_MESSAGE = "Escribe la razón social";

/** Lo que el formulario muestra al abrir: nombre tal cual, RUT con puntos. */
export function datosLegalesDraftOf(org: { name: string; rut: string | null }): DatosLegalesDraft {
  return { name: org.name, rut: org.rut ? formatRut(org.rut) : "" };
}

/** true si algún campo difiere de lo que se mostró al abrir (espacios aparte). */
export function datosLegalesDirty(initial: DatosLegalesDraft, draft: DatosLegalesDraft): boolean {
  return draft.name.trim() !== initial.name.trim() || draft.rut.trim() !== initial.rut.trim();
}

export type DatosLegalesPatch =
  | { kind: "unchanged" }
  | { kind: "invalid"; field: "name" | "rut"; message: string }
  | { kind: "patch"; body: MyOrganizationUpdateBody };

/**
 * El body del PATCH con sólo lo que cambió. Un RUT vacío borra el guardado
 * (`rut: null`); uno escrito debe cuadrar en módulo 11 (normalizeRut) y
 * viaja canónico.
 */
export function datosLegalesPatch(
  initial: DatosLegalesDraft,
  draft: DatosLegalesDraft
): DatosLegalesPatch {
  const name = draft.name.trim();
  const rutInput = draft.rut.trim();
  const nameChanged = name !== initial.name.trim();
  const rutChanged = rutInput !== initial.rut.trim();
  if (!nameChanged && !rutChanged) return { kind: "unchanged" };
  if (nameChanged && name.length === 0) {
    return { kind: "invalid", field: "name", message: NAME_REQUIRED_MESSAGE };
  }
  const body: MyOrganizationUpdateBody = {};
  if (nameChanged) body.name = name;
  if (rutChanged) {
    if (rutInput === "") {
      body.rut = null;
    } else {
      const normalized = normalizeRut(rutInput);
      if (!normalized) return { kind: "invalid", field: "rut", message: RUT_INVALID_MESSAGE };
      body.rut = normalized;
    }
  }
  return { kind: "patch", body };
}
